import React, { useMemo } from 'react';
import type { StrokeSample } from '../../../features/dashboards/rowingArcTypes';
import { Card } from '../../ui/Card';
import { getStatusTextClass } from './arcVisualSystem';
import { calculateStatus, getMetricTargetRange, type MarkerStatus } from './arcMetrics';
type TrendTone = 'positive' | 'negative' | 'neutral';

interface UnifiedMetricTileProps {
  label: string;
  average: number | null;
  current: number | null;
  previous?: number | null;
  /** When true, current value is shown as N/A (e.g. catch gap with no pre-catch window). */
  valueNotApplicable?: boolean;
  status?: MarkerStatus;
  trendTone: TrendTone;
  target: {
    min?: number;
    max?: number;
    label: string;
  };
  unit: string;
}

const trendToneClass: Record<TrendTone, string> = {
  positive: getStatusTextClass('good'),
  negative: getStatusTextClass('bad'),
  neutral: 'text-rowing-gray-300',
};

const calculateDeviation = (value: number, target: { min?: number; max?: number }): number => {
  if (target.min !== undefined && target.max !== undefined) {
    if (value < target.min) return target.min - value;
    if (value > target.max) return value - target.max;
    const midpoint = (target.min + target.max) / 2;
    return Math.abs(value - midpoint);
  }

  if (target.max !== undefined) {
    return value <= target.max ? target.max - value : value - target.max;
  }

  if (target.min !== undefined) {
    return value >= target.min ? value - target.min : target.min - value;
  }

  return 0;
};

const calculateTrendTone = (
  current: number,
  previous: number | undefined,
  target: { min?: number; max?: number }
): TrendTone => {
  if (previous === undefined) return 'neutral';

  const currentDeviation = calculateDeviation(current, target);
  const previousDeviation = calculateDeviation(previous, target);

  if (Math.abs(currentDeviation - previousDeviation) < 0.1) {
    return 'neutral';
  }

  return currentDeviation < previousDeviation ? 'positive' : 'negative';
};

const UnifiedMetricTile: React.FC<UnifiedMetricTileProps> = ({
  label,
  average,
  current,
  previous,
  valueNotApplicable = false,
  status,
  trendTone,
  target,
  unit,
}) => {
  const trend = useMemo(() => {
    if (previous == null || current == null) return 'flat';
    const delta = current - previous;
    if (Math.abs(delta) < 0.1) return 'flat';
    return delta > 0 ? 'up' : 'down';
  }, [current, previous]);

  return (
    <Card variant="interactive" className="p-3 sm:p-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-rowing-gray-700 sm:mb-3">
        {label}
      </h4>

      <div className="mb-2 flex items-end gap-3 sm:mb-3 sm:gap-4">
        <div className="flex-1">
          <div className="text-[10px] text-rowing-gray-500 mb-1 uppercase tracking-wide">Avg</div>
          <div className="text-xl font-bold text-rowing-gray-900 sm:text-2xl">
            {average == null ? '—' : average.toFixed(1)}
            {average != null && (
              <span className="text-sm font-normal text-rowing-gray-500 ml-0.5">{unit}</span>
            )}
          </div>
        </div>

        <div className="flex-1">
          <div className="text-[10px] text-rowing-gray-500 mb-1 uppercase tracking-wide">
            Current
          </div>
          <div className="flex items-end gap-1">
            <div
              className={`text-xl font-bold sm:text-2xl ${
                valueNotApplicable || current == null || !status
                  ? 'text-rowing-gray-400'
                  : getStatusTextClass(status)
              }`}
            >
              {valueNotApplicable ? 'N/A' : current == null ? '—' : current.toFixed(1)}
              {!valueNotApplicable && current != null && (
                <span className="text-sm font-normal text-rowing-gray-500 ml-0.5">{unit}</span>
              )}
            </div>
            <div
              className="flex items-center mb-1"
              aria-hidden={current == null || previous == null}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={
                  current != null && previous != null && trend === 'up'
                    ? trendToneClass[trendTone]
                    : trendToneClass.neutral
                }
              >
                <path d="m5 12 7-7 7 7" />
                <path d="M12 19V5" />
              </svg>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={
                  current != null && previous != null && trend === 'down'
                    ? trendToneClass[trendTone]
                    : trendToneClass.neutral
                }
              >
                <path d="M12 5v14" />
                <path d="m19 12-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-3 mt-3 border-t border-rowing-gray-100">
        <div className="text-xs text-rowing-gray-600">{target.label}</div>
      </div>
    </Card>
  );
};

interface MetricTilesProps {
  seatStrokes?: StrokeSample[];
  activeStrokeIndex: number;
  targets: {
    catchAngle: { min: number; max: number };
    finishAngle: { min: number; max: number };
    catchGap: { max: number };
    finishSlip: { min: number; max: number };
  };
}

const ROLLING_WINDOW = 10;
const buildArcTargets = (targets: MetricTilesProps['targets']) => ({
  catchAngleMin: targets.catchAngle.min,
  catchAngleMax: targets.catchAngle.max,
  finishAngleMin: targets.finishAngle.min,
  finishAngleMax: targets.finishAngle.max,
  catchGapMax: targets.catchGap.max,
  finishSlipMin: targets.finishSlip.min,
  finishSlipMax: targets.finishSlip.max,
  warningBand: 0.05,
});

const MetricTiles: React.FC<MetricTilesProps> = ({
  seatStrokes = [],
  activeStrokeIndex,
  targets,
}) => {
  const arcTargets = useMemo(() => buildArcTargets(targets), [targets]);

  const metrics = useMemo(() => {
    if (seatStrokes.length === 0) {
      return {
        catchAngle: { avg: null, current: null, previous: null },
        finishAngle: { avg: null, current: null, previous: null },
        catchGap: { avg: null, current: null, previous: null, notApplicable: false },
        finishSlip: { avg: null, current: null, previous: null },
      };
    }

    const activeIndex = Math.min(seatStrokes.length - 1, Math.max(0, activeStrokeIndex));
    const currentStroke = seatStrokes[activeIndex];
    const previousStroke = activeIndex > 0 ? seatStrokes[activeIndex - 1] : undefined;
    const rollingStart = Math.max(0, activeIndex - (ROLLING_WINDOW - 1));
    const rollingWindow = seatStrokes.slice(rollingStart, activeIndex + 1);
    const rollingGapApplicable = rollingWindow.filter(
      (s) => s.catchSlipApplicable !== false && s.catchSlipConfidence !== 'low'
    );

    return {
      catchAngle: {
        avg: rollingWindow.reduce((sum, s) => sum + s.catchAngleDeg, 0) / rollingWindow.length,
        current: currentStroke?.catchAngleDeg ?? null,
        previous: previousStroke?.catchAngleDeg,
      },
      finishAngle: {
        avg: rollingWindow.reduce((sum, s) => sum + s.finishAngleDeg, 0) / rollingWindow.length,
        current: currentStroke?.finishAngleDeg ?? null,
        previous: previousStroke?.finishAngleDeg,
      },
      catchGap: {
        avg:
          rollingGapApplicable.length === 0
            ? null
            : rollingGapApplicable.reduce((sum, s) => sum + s.catchSlipDeg, 0) /
              rollingGapApplicable.length,
        current:
          currentStroke?.catchSlipApplicable === false ||
          currentStroke?.catchSlipConfidence === 'low'
            ? null
            : (currentStroke?.catchSlipDeg ?? null),
        previous: previousStroke?.catchSlipDeg,
        notApplicable:
          currentStroke?.catchSlipApplicable === false ||
          currentStroke?.catchSlipConfidence === 'low',
      },
      finishSlip: {
        avg: rollingWindow.reduce((sum, s) => sum + s.finishSlipDeg, 0) / rollingWindow.length,
        current: currentStroke?.finishSlipDeg ?? null,
        previous: previousStroke?.finishSlipDeg,
      },
    };
  }, [seatStrokes, activeStrokeIndex]);

  return (
    <div className="grid grid-cols-1 min-[400px]:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
      <UnifiedMetricTile
        label="Catch Angle"
        average={metrics.catchAngle.avg}
        current={metrics.catchAngle.current}
        previous={metrics.catchAngle.previous}
        status={
          metrics.catchAngle.current == null
            ? undefined
            : calculateStatus(
                metrics.catchAngle.current,
                targets.catchAngle.min,
                targets.catchAngle.max
              )
        }
        trendTone={calculateTrendTone(
          metrics.catchAngle.current ?? 0,
          metrics.catchAngle.previous,
          targets.catchAngle
        )}
        target={getMetricTargetRange('catch', arcTargets)}
        unit="°"
      />
      <UnifiedMetricTile
        label="Finish Angle"
        average={metrics.finishAngle.avg}
        current={metrics.finishAngle.current}
        previous={metrics.finishAngle.previous}
        status={
          metrics.finishAngle.current == null
            ? undefined
            : calculateStatus(
                metrics.finishAngle.current,
                targets.finishAngle.min,
                targets.finishAngle.max
              )
        }
        trendTone={calculateTrendTone(
          metrics.finishAngle.current ?? 0,
          metrics.finishAngle.previous,
          targets.finishAngle
        )}
        target={getMetricTargetRange('finish', arcTargets)}
        unit="°"
      />
      <UnifiedMetricTile
        label="Catch Gap"
        average={metrics.catchGap.avg}
        current={metrics.catchGap.current}
        previous={metrics.catchGap.previous}
        valueNotApplicable={metrics.catchGap.notApplicable}
        status={
          metrics.catchGap.notApplicable || metrics.catchGap.current == null
            ? undefined
            : calculateStatus(metrics.catchGap.current, undefined, targets.catchGap.max)
        }
        trendTone={
          metrics.catchGap.notApplicable
            ? 'neutral'
            : calculateTrendTone(
                metrics.catchGap.current ?? 0,
                metrics.catchGap.previous,
                targets.catchGap
              )
        }
        target={getMetricTargetRange('gap', arcTargets)}
        unit="°"
      />
      <UnifiedMetricTile
        label="Finish Slip"
        average={metrics.finishSlip.avg}
        current={metrics.finishSlip.current}
        previous={metrics.finishSlip.previous}
        status={
          metrics.finishSlip.current == null
            ? undefined
            : calculateStatus(
                metrics.finishSlip.current,
                undefined,
                targets.finishSlip.max,
                arcTargets.warningBand
              )
        }
        trendTone={calculateTrendTone(
          metrics.finishSlip.current ?? 0,
          metrics.finishSlip.previous,
          targets.finishSlip
        )}
        target={getMetricTargetRange('slip', arcTargets)}
        unit="°"
      />
    </div>
  );
};

export default MetricTiles;
